import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { Spinner } from '../ui/common.js';

function dDot(iso?: string): string {
  return iso && iso.length >= 10 ? iso.slice(0, 10).replace(/-/g, '.') : '';
}

// 사규 — (주)디엑스테크 취업규칙 (제정 2016.07.22, R1.1). 원문 사규집 기준으로 반영.
interface RuleArticle { t: string; b: string }
interface RuleChapter { ch: string; articles: RuleArticle[] }
const RULE_PREAMBLE =
  '이 취업규칙은 근로기준법에 의거하여 (주)디엑스테크 소속직원의 근로 조건을 확립함을 목적으로 제정한다. 이 취업규칙에 입각하여 회사는 직원의 인격과 자주성을 존중하고 고용의 안정, 능력주의와 업적주의 및 적정급여의 원칙에 따라 공정한 인사관리를 기하여 인화의 문화를 이룩하며, 직원은 이 규칙을 준수하여 직장의 질서를 지키고 자기의 맡은 바 직무를 최선을 다하여 성실하게 수행함으로써 회사의 발전을 위하여 노력한다.';
const COMPANY_RULES: RuleChapter[] = [
  { ch: '제1장 총칙', articles: [
    { t: '제1조 (적용원칙)', b: '① 직원의 일상복무에 관한 사항은 법령으로 정하여진 것 이외에는 이 규칙과 이에 의하여 제정된 제규정이 정하는 바에 의한다.\n② 이 규칙은 등기임원 중 상근직에는 직원과 동일하게 적용하기로 한다.\n③ 이 규칙은 비상근 직원에 대하여는 적용하지 아니한다.' },
  ]},
  { ch: '제2장 복무 · 제1절 일반준수사항', articles: [
    { t: '제2조 (복무원칙)', b: '① 직원은 정관과 제반규정 및 관련법규를 성실히 지키고 상사의 직무상 명령에 충실히 복종하여야 한다.\n② 담당 직책을 성실하고 신속정확하게 완수하여야 한다.\n③ 회사의 인사이동 명령에 따라야 한다.\n④ 상급자·업무상 지시자의 지시를 성실히 이행하여야 하며 불성실 시 감봉 등의 불이익 처분을 받을 수 있다.\n⑤ 언행에 조심하고 예절을 존중하며 회사의 신용과 명예를 손상하지 아니한다.\n⑥ 사장의 승인 없이 타 직무 또는 사업에 종사할 수 없다.\n⑦ 계약서상 업무를 수개월 불성실하거나 업무능력이 현저히 떨어진다고 판단되면 회사는 업무 변경을 명할 수 있으며, 이 경우 1개월 이내 새 업무에 대한 재계약을 체결한다.\n⑧ 재고용계약은 선행 계약 만기 1개월 전까지 체결하며, 직전 1년간 업무·기여도 평가를 반영한다.\n⑨ 전거·전적·개명 등 이력사항 변경 시 발생일로부터 14일 이내 회사에 신고한다.' },
    { t: '제3조 (재산권 및 기밀엄수)', b: '직원의 복무기간 중 개발 또는 성취된 물적·지적 재산의 모든 소유권은 (주)디엑스테크에 있으며, 고용·계약기간 중 또는 만료 후 3년 내에 이를 경쟁사에 고의 또는 타의로 유출한 경우 민·형사상의 책임을 진다.' },
    { t: '제4조 (증여·향응·금전차용의 금지)', b: '직원은 직·간접을 불문하고 어떠한 명목으로도 회사와 금전 또는 기타 거래를 하고 있는 자로부터 증여·향응을 받거나 금전을 차용하여서는 아니 된다.' },
    { t: '제5조 (보증행위의 금지)', b: '직원은 회사에 대한 타인의 채무보증 및 신원보증행위를 하여서는 아니 된다.' },
    { t: '제6조 (손해배상)', b: '직원이 고의 또는 과실로 회사재산에 피해를 입혔을 경우 이를 배상하여야 한다.' },
    { t: '제7조 (고용계약서의 제출)', b: '임직원은 회사가 별도로 정한 고용계약서를 제출하는 것을 원칙으로 한다. 단, 사장이 인정하는 경우 면제할 수 있다.' },
  ]},
  { ch: '제2장 복무 · 제2절 채용', articles: [
    { t: '제8조 (직원채용)', b: '직원의 채용에 관하여는 "인사규정"이 정하는 바에 의한다.' },
  ]},
  { ch: '제2장 복무 · 제3절 근무시간·휴게시간·유급휴일', articles: [
    { t: '제9조 (근무시간·휴게시간)', b: '① 근무시간은 휴게·식사시간을 제외하고 1일 8시간, 1주 40시간을 기준으로 한다.\n · 개발/운영/지원부서: 09:00 시업 ~ 18:00 종업 (점심 12:00–13:00)\n · 유연근무: 10:00 시업 ~ 19:00 종업 (점심 12:00–13:00)\n② 개발연구·특수업무 종사자는 업무성격에 따라 주야간 변동·교대근무 등으로 탄력 운영할 수 있다.\n③ 그 외에도 사장이 인정하는 경우 근무시간을 변경할 수 있다.\n④ 출장 중인 직원의 근무시간은 1일 8시간으로 한다. 단, 별도로 정한 경우 그 시간으로 한다.' },
    { t: '제10조 (휴일)', b: '유급휴일은 다음과 같다. — 일요일 및 법정공휴일 / 5월 1일(근로자의 날) / 기타 정부가 정하는 공휴일 / 회사가 휴일로 정하는 날.' },
    { t: '제11조 (시간외근무 및 휴일근무)', b: '① 업무상 필요 시 해당 직원의 동의를 얻어 시간 외 근무 또는 휴일근무를 하게 할 수 있다.\n② 시간 외·휴일근무에 대하여는 따로 시간 외 근무수당을 지급한다.' },
  ]},
  { ch: '제2장 복무 · 제4절 출퇴근 및 결근', articles: [
    { t: '제12조 (출퇴근 및 결근)', b: '① 직원은 시업시간 이전에 출근하여야 한다.\n② 종업시간 이후에 퇴근하며 필요시 퇴근보고를 지시할 수 있다.\n③ 부득이한 결근 시 사전에 결근계를 제출하며, 불가피하게 사전 제출이 곤란하면 사후 보고한다(미보고 시 무단결근).\n④ 질병으로 5일 이상 계속 결근 시 의사진단서를 제출한다.\n⑤ 결근 시 연·월차로 공제하고 부족분은 무급 처리한다.' },
    { t: '제13조 (지각 및 조퇴)', b: '① 시업 후 2시간 이내 출근을 지각이라 한다.\n② 시업 4시간 경과 후 종업 이전에 퇴근함을 조퇴라 한다.\n③ 지각·조퇴가 2회인 경우 1일 결근으로 처리한다.' },
    { t: '제14조 (외출 및 면회)', b: '근무시간 중 직무 이외의 사유로 인한 외출·면회는 원칙적으로 불허하되, 직속상관의 허가를 득한 경우 허가한다.' },
  ]},
  { ch: '제2장 복무 · 제5절 휴가', articles: [
    { t: '제15조 (휴가의 종류)', b: '휴가는 법정·청원·특별 및 인병휴가로 구분한다.' },
    { t: '제16조 (유급휴가)', b: '① 회사는 근로기준법에 의한 연·월차 유급휴가를 준다.\n② 청원휴가·인병휴가·특별휴가 등을 줄 수 있다.\n③ 제반 휴가는 신청 후 회사의 허가를 받아야 한다.\n④ 유급휴가 관련 보상수당은 지급하지 아니함을 원칙으로 한다.\n⑤ 휴가 관련 세부사항은 "휴가운영세칙"에 따른다.' },
  ]},
  { ch: '제2장 복무 · 제6절 휴직 및 복직', articles: [
    { t: '제17조 (휴직의 구분)', b: '휴직은 명령휴직·인병휴직·청원휴직으로 구분한다.' },
    { t: '제18조 (명령휴직)', b: '사장은 형사사건 기소자(확정판결 시까지), 전염성·정신병으로 병세악화 우려자, 정당한 사유 없이 월출근일수의 1/2 이상 결근하거나 월 4회 이상 결근하는 상태가 2월을 초과하는 자에 대하여 휴직을 명할 수 있다.' },
    { t: '제19조 (인병휴직)', b: '직원이 질병·부상으로 출근이 불가능한 경우 인병휴직을 출원할 수 있다.' },
    { t: '제20조 (청원휴직)', b: '직원은 사장이 인정하는 경우 청원휴직을 출원할 수 있다.' },
    { t: '제21조 (휴직기간)', b: '① 명령휴직 기간은 그때마다 사장이 정한다.\n② 인병·청원휴직 기간은 본인 신청에 의해 사장이 승인하는 기간 내에서 정한다.' },
    { t: '제22조 (휴직 및 복직절차)', b: '휴직·복직 시 인병휴직은 종합병원 진단서를, 청원휴직은 관계 소명자료를 첨부하여 소속부서장·인사담당부서장을 경유해 사장의 승인을 받는다. 만료 후에도 사유 미소멸로 출근 불가 시 소명하여 연장을 출원한다.' },
    { t: '제23조 (휴직자의 보수)', b: '휴직자의 보수는 급여규정이 정하는 바에 의한다.' },
  ]},
  { ch: '제3장 인사 및 상벌', articles: [
    { t: '제24조 (인사)', b: '직원의 채용·승진·이동·근무평정·상벌·인사위원회 등에 관하여는 따로 "인사규정"이 정하는 바에 의한다.' },
    { t: '제25조 (상벌)', b: '직원의 상벌에 관하여는 따로 인사규정이 정하는 바에 의한다.' },
  ]},
  { ch: '제4장 급여 / 복지', articles: [
    { t: '제26조 (보수)', b: '직원에 대한 급여 및 복지 등에 관하여는 따로 급여복지규정이 정하는 바에 의한다.' },
  ]},
  { ch: '제5장 퇴직', articles: [
    { t: '제27조 (퇴직의 구분)', b: '직원의 퇴직은 의원퇴직·정년퇴직·징계퇴직 및 당연퇴직으로 구분한다.' },
    { t: '제28조 (의원퇴직)', b: '① 퇴직하고자 하는 경우 사직원을 제출하며 사무인계에 지장이 없도록 한다.\n② 사직원은 소속부서장·경영지원부서장을 경유하여 사장의 승인을 받는다.' },
    { t: '제29조 (정년퇴직)', b: '① 직원의 정년은 만 65세로 한다.\n② 회사와 고용계약을 체결한 직원은 계약만기일을 퇴직일로 본다.' },
    { t: '제30조 (징계퇴직)', b: '직원의 징계퇴직에 관하여는 인사규정이 정하는 바에 의한다.' },
    { t: '제31조 (당연퇴직)', b: '의원·징계퇴직으로 처리되지 않는 다음의 경우 당연퇴직으로 처리한다. — 사망, 회사 임원 선임, 금치산·한정치산·파산선고, 금고 이상 확정판결(교통사고 집행유예 제외), 휴직만료 후 14일 내 미복직, 대외파견 만료 후 2주 내 미복직.' },
    { t: '제32조 (직원퇴직금)', b: '퇴직하는 직원에 대한 퇴직금 지급은 따로 "급여복지규정"이 정하는 바에 의한다.' },
    { t: '제33조 (임원퇴직금)', b: '임원으로 재직하다 임기를 다한 자에게 퇴직금을 지급한다. ① 산정은 포상금·성과급을 제외한 기본급을 평균임금으로 한다. ② 지급률은 급여·복지규정에 의한다. ③ 기산일은 등기부 등재일 기준. ④ 기타는 직원퇴직금 지급규정을 준용한다.' },
  ]},
  { ch: '제6장 기타', articles: [
    { t: '제34조 (사무인수)', b: '사무인수 인계에 따른 세부사항은 "사무인수세칙"에 의한다.' },
    { t: '제35조 (재해보상)', b: '회사는 직원이 업무상 질병 또는 사망한 경우 법령이 정하는 바에 따라 재해보상을 행한다.' },
    { t: '제36조 (교육훈련)', b: '회사는 직원의 업무능력 향상을 위하여 교육훈련을 지원하며, 세부사항은 "직원연수세칙"에서 정한다.' },
  ]},
  { ch: '부칙', articles: [
    { t: '제1조 (시행일)', b: '이 규칙은 2016년 07월 22일부터 시행한다.' },
    { t: '※ 관련 규정', b: '인사규정 · 급여복지규정' },
    { t: '※ 관련 세칙', b: '휴가운영세칙 · 사무인수세칙 · 직원연수세칙' },
  ]},
];

// 휴가운영세칙 — (주)디엑스테크 (제정 2016.07.22). 취업규칙 제16조⑤에서 위임한 세칙. 원문 사규집 기준.
const LEAVE_RULES: RuleChapter[] = [
  { ch: '제1장 총칙', articles: [
    { t: '제1조 (목적)', b: '이 세칙은 직원에게 적당한 휴식시간을 제공하여 업무의 효율성 및 근로의욕을 증진시키기 위함이다.' },
    { t: '제2조 (신청방법)', b: '직원이 규정에 정한 휴가를 사용할 시에는 휴가원을 제출하여 회사의 허가를 받아야 한다. 단, 회사의 운영 및 업무상황을 고려하여 사용일자 및 기간은 협의 하에 조정할 수 있다.' },
    { t: '제3조 (휴가의 보상)', b: '① 회사의 귀책사유 이외에는 유급휴가와 관련된 보상수당은 지급하지 아니한다.\n② 경조 및 특별휴가 등의 미사용 시 이에 대한 별도의 보상은 없다.' },
    { t: '제4조 (기간산정)', b: '① 휴가기간 중 휴일이 개재된 경우 이를 휴가일수에 통산한다.\n② 연·월차 휴가일수는 당해연도 중에 한하여 적치 또는 분할하여 사용할 수 있으며, 연차 휴가일수가 20일을 초과할 경우에는 그 초과하는 일수에 대하여 평균임금을 지급하고 휴가를 주지 아니할 수 있다.' },
  ]},
  { ch: '제2장 종류', articles: [
    { t: '제5조 (휴가의 종류)', b: '휴가는 유급휴가·청원휴가·특별휴가·인병휴가 및 공가로 구분한다.' },
    { t: '제6조 (유급휴가)', b: '회사는 아래와 같이 유급휴가를 준다.\n① 월차휴가 : 1개월 개근 시 1일의 유급휴가 발생.\n② 연차휴가 : 1년간 개근 시 10일, 9할 이상 출근 시 8일의 연차휴가 발생.\n  가) 1년 이상 근속자는 매 1년 초과 시 1일을 가산하여 연차휴가 발생.\n  나) 근속년수 1년 미만인 자는 연차휴가 대상에서 제외한다.\n③ 생리휴가 : 여직원에 대하여 월 1일.\n④ 출산휴가(본인) : 산전후 총 60일간 휴가 발생. 휴가 중에는 기본급의 80%를 지급한다.\n⑤ 유급휴가의 산정기간은 1월 1일부터 12월 31일까지로 한다.' },
    { t: '제7조 (청원휴가)', b: '직원이 다음 각 호에 해당한 경우에는 소정기간 범위 내에서 청원휴가를 허가할 수 있다.\n① 축하 휴가\n  가) 본인의 결혼 : 6일\n  나) 자녀의 결혼 : 3일\n  다) 형제·자매의 결혼 : 1일\n  라) 부모(배우자의 부모 포함) 회갑 : 1일\n  마) 자녀 출산 : 1일\n② 조의 휴가\n  가) 부모 및 배우자의 사망 : 5일\n  나) 처의 사망 : 3일\n  다) 조부모의 사망 : 2일\n  라) 형제자매 사망 시 : 2일\n③ 기타 경조사 발생 시 : 3일 이내에서 회사에서 인정한 일수.' },
    { t: '제8조 (특별휴가)', b: '직원이 다음 각 호의 1에 해당하는 경우에는 소정기간 범위 내에서 특별휴가를 허가할 수 있다.\n· 직원의 체력단련을 위한 휴가 : 6일 이내\n· 업무상 특별한 공로나 포상으로 사장이 인정하는 휴가 : 6일 이내' },
    { t: '제9조 (인병휴가)', b: '① 직원이 업무상 발생한 부상 또는 질병으로 인하여 출근이 불가능한 경우에는 치료에 소요되는 기간 동안 인병휴가를 허가할 수 있다.\n② 휴가기간은 연 누계 3개월 범위 내에서 허가할 수 있다. 단, 미사용 연·월차 휴가가 있는 자는 잔여 연·월차휴가를 사용한 후부터 인병휴가를 인정한다.\n③ 인병휴가 기간이 1개월을 초과할 경우 기본급의 80%를 지급한다.' },
    { t: '제10조 (공가)', b: '직원이 다음 각 호의 1에 해당하는 경우에는 필요한 기간 동안 공가를 허가할 수 있다.\n· 병역검사나 예비군·민방위의 근무소집연습 또는 검열점호가 있을 때.\n· 공무와 관련하여 법원 등에 소환될 때.\n· 법률의 규정에 의하여 투표에 참가하거나 기타 업무를 수행할 때.\n· 천재지변·화재·교통차단·기타 재해의 사유로 출근이 불가능한 때.' },
  ]},
  { ch: '부칙', articles: [
    { t: '제1조 (시행일)', b: '이 세칙은 2016년 07월 22일부터 시행한다.' },
  ]},
];

// 직제규정 — (주)디엑스테크 (제정 2016.07.22). 회사의 조직 및 직무권한을 규정. 원문 사규집 기준.
// ⚠️ 원문에 온라인게임사업부 등 레거시 템플릿 조직명이 잔존 — 실제 조직도로 갱신 필요.
const ORG_RULES: RuleChapter[] = [
  { ch: '제1장 총칙', articles: [
    { t: '제1조 (목적)', b: '이 규정은 회사의 조직 및 직무권한에 관한 사항을 규정함으로써 체계있는 업무수행과 능률적인 관리운영을 도모함을 목적으로 한다.' },
    { t: '제2조 (조직 편제)', b: '① 회사는 4개 사업부, 2개 지원부 및 기획실을 둔다.\n② 각 사업부 및 지원부에는 하부조직으로 부·실·팀 및 과를 둘 수 있다.\n③ 사업부와 지원부 이외의 조직으로서 국내와 국외에 사무소를 설치하거나 주재원을 둘 수 있다.' },
    { t: '제3조 (직무의 책임과 권한)', b: '① 각 단위조직에는 그에 합당한 업무를 부여한다.\n② 단위부서와 팀에는 그에 합당한 직무의 책임과 권한을 부여한다.' },
  ]},
  { ch: '제2장 직무분장', articles: [
    { t: '제4조 (직무분장)', b: '① 단위조직의 업무분장은 별도의 직무분장세칙에서 정한다.\n② 직무분장세칙과 관련된 세부사항은 사장이 정한다.' },
  ]},
  { ch: '제3장 직무전결', articles: [
    { t: '제5조 (직무전결)', b: '① 단위조직의 업무관련 책임과 권한에 대하여는 별도의 직무전결세칙에서 정한다.\n② 직무전결세칙과 관련된 세부사항은 사장이 정한다.' },
  ]},
  { ch: '제4장 세칙제정', articles: [
    { t: '제6조 (세칙제정)', b: '① 단위조직의 업무와 관련하여 별도의 세칙을 제정할 수 있다.\n② 세칙제정 및 이와 관련된 세부사항은 사장이 정한다.' },
  ]},
  { ch: '제5장 기준제정', articles: [
    { t: '제7조 (기준제정)', b: '① 조직의 업무와 관련하여 별도의 기준을 제정할 수 있다.\n② 기준제정 및 이와 관련된 세부사항은 사장이 정한다.' },
  ]},
  { ch: '제6장 사업부', articles: [
    { t: '제8조 (사업부의 구성)', b: '① 사업부는 다음의 4개로 구성된다. — 1. 온라인게임 사업부 / 2. 인터넷 사업부 / 3. 특수 사업부 / 4. 해외 사업부.\n② 각 사업부의 구성은 별도의 직무분장세칙에서 정하기로 한다.' },
    { t: '제9조 (사업부장)', b: '① 사업부에는 사업부장을 두고 이사급 임원으로 보한다. 해당 사업부에 이사급의 임원이 결원일 경우 차위자 중에서 이를 임명한다.\n② 사업부장은 해당 사업부의 사무를 통할하고 소속직원을 지휘감독한다.\n③ 사업부장이 유고 시에는 해당 사업부의 차위자가 그 직무를 대리한다. 다만, 필요한 경우에는 사장이 직무대리자를 지명할 수 있다.' },
    { t: '제10조 (하부조직)', b: '① 사업부는 하부조직으로 부·실·팀 및 과를 둘 수 있다.\n② 단위 하부조직에는 선임 또는 3급 이상의 직원을 단위 책임자로 보한다.\n③ 단위책임자는 사업부장을 보좌하며, 그 명을 받아 담당하는 사무를 통할한다.' },
  ]},
  { ch: '제7장 지원부', articles: [
    { t: '제11조 (지원부의 구성)', b: '① 지원부는 다음의 2개로 구성된다. — 1. 마케팅본부 / 2. 경영지원부.\n② 각 지원부의 구성은 별도의 직무분장세칙에서 정하기로 한다.' },
    { t: '제12조 (지원부장)', b: '① 지원부에는 지원부장을 두고 이사급 임원으로 보한다. 해당 지원부에 이사급의 임원이 결원일 경우 차위자 중에서 이를 임명한다.\n② 지원부장은 해당 지원부의 사무를 통할한다.\n③ 지원부장이 유고 시에는 해당 지원부의 차위자가 그 직무를 대리한다. 다만, 필요한 경우에는 사장이 직무대리자를 지명할 수 있다.' },
    { t: '제13조 (하부조직)', b: '① 지원부는 하부조직으로 부·실·팀 및 과를 둘 수 있다.\n② 단위 하부조직에는 선임 또는 3급 이상의 직원을 단위 책임자로 보한다.\n③ 단위책임자는 지원부장을 보좌하며, 그 명을 받아 담당하는 사무를 통할한다.' },
  ]},
  { ch: '제8장 기획실', articles: [
    { t: '제14조 (기획실의 업무)', b: '기획실은 다음의 업무를 수행한다. — ① 회사의 중장기 경영기획 / ② 해외경영정보 분석 및 기획.' },
    { t: '제15조 (기획실장)', b: '① 기획실에는 기획실장을 두고 이사급 임원으로 보한다. 해당 부서에 이사급의 임원이 결원일 경우 차위자 중에서 이를 임명한다.\n② 기획실장은 해당 부서의 사무를 통할하고 소속직원을 지휘감독한다.\n③ 기획실장의 유고 시에는 해당 직원 중 차위자가 그 직무를 대리한다.' },
    { t: '제16조 (프로젝트팀)', b: '① 제3조의 조직 외에 특정 프로젝트의 수행을 위하여 별도의 팀을 구성할 수 있다.\n② 전항의 경우 팀장은 사장이 별도로 임명할 수 있다.' },
    { t: '제17조 (위원회)', b: '업무상 필요하다고 인정하는 때에는 위원회를 설치할 수 있다.' },
    { t: '제18조 (사무소·주재원)', b: '사무소 및 주재원의 운용과 사무에 관하여 필요한 사항은 사장이 따로 정한다.' },
  ]},
  { ch: '부칙 · 관련 세칙', articles: [
    { t: '제1조 (시행일)', b: '이 규정은 2000년 7월 1일부터 시행한다.' },
    { t: '제2조 (시행일)', b: '이 규정은 2001년 9월 13일부터 시행한다.' },
    { t: '※ 관련 세칙', b: '직무분장세칙 · 직무전결세칙' },
    { t: '※ 관련 기준', b: '인장 및 인감 관리 기준' },
  ]},
];

// 직무분장세칙 — (주)디엑스테크 (제정 2000.07.01). 직제규정 제4조에서 위임한 세칙. 원문 사규집 기준.
// ⚠️ 원문에 온라인게임사업부·행복동팀 등 레거시 템플릿 조직명이 잔존 — 실제 조직 편제로 갱신 필요.
const JOB_ASSIGN_RULES: RuleChapter[] = [
  { ch: '제1절 총칙', articles: [
    { t: '제1조 (목적)', b: '이 세칙은 회사 조직의 세부적인 사항을 정하는 것을 목적으로 한다.' },
  ]},
  { ch: '제2절 사업부', articles: [
    { t: '제2조 (온라인게임사업부)', b: '① 사업부는 다음 3개팀으로 구성된다. — 게임기획팀 / 게임개발팀 / 디자인팀.\n② 게임기획팀은 게임의 기획 및 원화작업을 담당한다.\n③ 게임개발팀은 게임관련 프로그램 작업을 총괄하여 담당한다.\n④ 디자인팀은 게임관련 캐릭터 및 디자인을 총괄하여 담당한다.' },
    { t: '제3조 (인터넷사업부)', b: '① 인터넷사업부는 다음 2개팀으로 구성된다. — 행복동팀 / 세컨드리눅스팀.\n② 행복동팀은 그래픽채팅인 행복동을 개발하는 업무를 담당한다.\n③ 세컨드리눅스팀은 게임관련 리눅스툴을 개발하는 업무를 담당한다.' },
    { t: '제4조 (특수사업부)', b: '① 특수사업부는 다음 2개팀으로 구성된다. — 게임포탈팀 / 신규사업팀.\n② 게임포탈팀은 포탈의 개발 및 업그레이드 업무를 담당한다.\n③ 신규사업팀은 신규진출사업(교육용게임·미니게임·웹게임)에 대한 기획 및 업무진행을 담당한다.' },
    { t: '제5조 (유통사업부)', b: '① 유통사업부는 다음 2개팀으로 구성된다. — 해외영업팀 / 국내유통팀.\n② 해외영업팀은 당사 및 타사관련 해외영업을 담당한다.\n③ 국내유통팀은 타사관련 게임을 국내에 유통하는 업무를 담당한다.' },
  ]},
  { ch: '제3절 지원부', articles: [
    { t: '제6조 (마케팅부)', b: '① 마케팅부는 다음 3개팀으로 구성된다. — 영업기획팀 / 네트웍관리팀 / 고객지원팀.\n② 영업기획팀은 영업의 기획 및 입금관리 등을 담당한다.\n③ 네트웍관리팀은 당사 게임공급관련 네트웍의 관리업무를 담당한다.\n④ 고객지원팀은 당사의 게임운영 업무를 담당한다.' },
    { t: '제7조 (경영지원부 구성)', b: '① 경영지원부는 다음의 2개팀으로 구성된다. — 회계팀 / 재무관리팀.\n② 회계팀은 회계 및 세무관련 업무를 총괄한다.\n③ 재무관리팀은 자금의 입출금·직원관리·기관관련 업무를 총괄한다.' },
    { t: '제8조 (기획실)', b: '기획실은 회사의 기획업무 및 사장의 지시업무를 담당한다.' },
  ]},
  { ch: '부칙', articles: [
    { t: '제1조 (시행일)', b: '이 규정은 2000년 7월 1일부터 시행한다.' },
  ]},
];

// 직무전결세칙 — (주)디엑스테크 (제정 2001.02.01). 직제규정 제5조에서 위임한 세칙. 원문 사규집 기준.
const DELEG_RULES: RuleChapter[] = [
  { ch: '본칙 (제1조~제12조)', articles: [
    { t: '제1조 (목적)', b: '이 세칙은 직위별로 직무전결사항을 규정함으로써 그 권한과 책임을 명확히 하고, 권한과 책임의 위임을 통하여 업무처리의 신속과 원활을 기하여 능률적인 조직운영과 부서별 책임경영제도를 정착화함을 목적으로 한다.' },
    { t: '제2조 (용어의 정의)', b: '이 규정에서 사용하는 용어의 정의는 다음과 같다.\n1. 직무 : 각 직위에 부과된 업무.\n2. 권한 : 직무자가 처리할 수 있는 행위의 한계.\n3. 입안 : 안을 작성하여 권한 보유자에게 결정을 구하는 것.\n4. 심의 : 의사결정상에 영향을 미치는 심사를 행하는 권한.\n5. 협의 : 입안사항에 대해 전결 전에 이의 심사를 협조함.\n6. 전결 : 해당 직무에 결정을 하는 권한과 책임을 지는 것.\n7. 결재 : 담당자로서 입안사항을 확정하는 행위.\n8. 보고 : 전결사항에 대해 상위자에게 중요사안을 보고함.\n9. 신청 : 해당 업무를 관장하는 부서에 청구하는 것.\n10. 지급 : 신청받은 사항을 지급하는 것.' },
    { t: '제3조 (적용원칙)', b: '① 직위별 전결사항에 관하여 따로 정함이 있는 경우를 제외하고는 이 세칙에 의한다.\n② 이 세칙 및 다른 내규에서 전결사항으로 명시하지 아니한 사항은, 그 업무의 성격과 경중에 따라 이 규정에서 정한 유사한 전결사항에 준하여 처리한다.' },
    { t: '제4조 (업무별 전결기준)', b: '업무별 전결기준은 별표1과 같다. (아래 「별표1 · 업무별 전결기준」 참조)' },
    { t: '제5조 (권한·책임 및 직무의 집행)', b: '① 결재권자는 전결기준에 따라 직무상의 권한을 자율적으로 성실히 행사하여야 한다.\n② 결재권자는 직무수행의 책임과 수행에 필요한 권한을 갖는다.\n③ 결재권자는 권한 행사에 따라 발생한 결과에 대하여 책임을 진다.' },
    { t: '제6조 (전결사항의 하부위임)', b: '전결권자는 그 책임하에 소속 부하책임자에게 그 전결사항의 일부를 처리하게 할 수 있다.' },
    { t: '제7조 (전결권자의 유고 시 처리)', b: '전결권자의 유고 시에는 그 직상위자가 전결한다.' },
    { t: '제8조 (보고)', b: '전결권자는 전결사항 중 특히 중요하다고 인정되는 사항은 상사에게 보고한다.' },
    { t: '제9조 (협의)', b: '① 업무를 수행함에 있어 다른 중요한 관계자가 있는 경우에는 반드시 협의하여 실시한다.\n② 협의 의견은 명확히 표시되어 결재권자에게 제시되어야 한다.' },
    { t: '제10조 (전결권자)', b: '전결권자는 직책 중심으로 다음과 같이 구분한다.\n· 대표이사\n· 임원 : 각 사업부·마케팅부·경영지원부·기획실·연구소 담당 임원.\n· 팀장 : 1개 팀을 담당하는 직원.' },
    { t: '제11조 (금액의 기준)', b: '① 이 세칙에서의 금액은 1건의 금액을 말하며, 여기서 1건이라 함은 결재문건 1건의 총액이 아니고 낱개의 증빙 한 건을 1건이라 한다.\n② 성격상 1건으로 처리하여야 할 사안을 분할하여 위임전결할 수 없다.' },
    { t: '제12조 (업무처리 및 전결 기준의 사항)', b: '업무처리 및 전결 사항은 다음과 같다.\n1. 각 사업부\n2. 마케팅부\n3. 경영지원부 (인사 · 예산 및 자금 · 총무 및 서무 · 회계)\n4. 기획' },
  ]},
  { ch: '부칙', articles: [
    { t: '제1조 (시행일)', b: '이 규정은 2001년 2월 1일부터 시행한다.' },
  ]},
];

// 별표1 · 업무별 전결기준 매트릭스 — 각 행의 전결권자(대표이사/임원/팀장). 원문 사규집 <별표1> 기준.
// g: 구분 그룹헤더 / s: 소분류 / c: 항목(전결권자 미표기) / t: 세부항목 + 전결권자(v)
type AuthLv = '대표' | '임원' | '팀장';
interface AuthRow { g?: string; s?: string; c?: string; t?: string; v?: AuthLv }
const DELEG_MATRIX: AuthRow[] = [
  { g: '1. 각 사업부' },
  { c: '1. 부서내 인력충원.' },
  { t: '- 필요인원 선정.', v: '팀장' },
  { t: '- 필요인원 1차 면접.', v: '임원' },
  { t: '- 필요인원 최종 선발.', v: '대표' },
  { c: '2. 부서내 인력배치.' },
  { t: '- 팀장급 이상.', v: '대표' },
  { t: '- 2,3급 및 매니저·책임·선임.', v: '임원' },
  { t: '- 기타직원.', v: '팀장' },
  { c: '3. 제품개발' },
  { t: '- 신규제품 개발', v: '대표' },
  { t: '- 기존제품 업그레이드', v: '임원' },
  { c: '4. 자산구입 결정.' },
  { t: '- 5백만원 이상.', v: '대표' },
  { t: '- 1백만원 이상 ~ 5백만원 미만', v: '임원' },
  { t: '- 기타', v: '팀장' },
  { c: '5. 일반경비 지출 결정.' },
  { t: '- 1백만원 이상.', v: '대표' },
  { t: '- 3십만원 이상 ~ 1백만원 미만', v: '임원' },
  { t: '- 기타', v: '팀장' },
  { c: '6. 기타사항' },
  { t: '- 대외기술 도입', v: '대표' },
  { t: '- 거래선 관리', v: '임원' },
  { t: '- 타사와의 계약 체결', v: '대표' },
  { g: '2. 마케팅부' },
  { c: '1. 부서내 인력충원.' },
  { t: '- 필요인원 선정.', v: '팀장' },
  { t: '- 필요인원 1차 면접.', v: '임원' },
  { t: '- 필요인원 최종 선발.', v: '대표' },
  { c: '2. 부서내 인력배치.' },
  { t: '- 팀장급 이상.', v: '대표' },
  { t: '- 2,3급 및 매니저·책임·선임.', v: '임원' },
  { t: '- 기타직원.', v: '팀장' },
  { c: '3. 일반업무.' },
  { t: '- 대외 전시회 참가.', v: '대표' },
  { t: '- 대외 정액제 중요 계약.', v: '대표' },
  { t: '- 대외 종량제 계약', v: '임원' },
  { t: '- 광고 판촉 기본계획 수립.', v: '임원' },
  { c: '4. 자산구입 결정.' },
  { t: '- 5백만원 이상.', v: '대표' },
  { t: '- 1백만원 이상 ~ 5백만원 미만', v: '임원' },
  { t: '- 기타', v: '팀장' },
  { c: '5. 기타 비용 지출 결정.' },
  { t: '- 1백만원 이상.', v: '대표' },
  { t: '- 3십만원 이상 ~ 1백만원 미만', v: '임원' },
  { t: '- 기타', v: '팀장' },
  { c: '6. 기타사항' },
  { t: '- 대외기술 도입', v: '대표' },
  { t: '- 거래선 관리', v: '임원' },
  { t: '- 해외 계약 체결', v: '대표' },
  { g: '3. 경영지원' },
  { s: '인사업무' },
  { c: '1. 부서내 인력충원.' },
  { t: '- 필요인원 선정.', v: '팀장' },
  { t: '- 필요인원 1차 면접.', v: '임원' },
  { t: '- 필요인원 최종 선발.', v: '대표' },
  { c: '2. 부서내 인력배치.' },
  { t: '- 팀장급 이상.', v: '대표' },
  { t: '- 2,3급 및 매니저·책임·선임.', v: '임원' },
  { t: '- 기타직원.', v: '팀장' },
  { t: '3. 부서간 인력이동.', v: '대표' },
  { c: '4. 직원의 승진 및 승급.' },
  { t: '- 직원의 승급', v: '대표' },
  { t: '- 개발직군의 등급 조정.', v: '대표' },
  { t: '- 일반직군의 조기 호봉 가급', v: '대표' },
  { c: '5. 직원의 연수.' },
  { t: '- 해외 및 팀장급 이상의 연수.', v: '대표' },
  { t: '- 국내 및 일반직원의 연수', v: '임원' },
  { c: '6. 직원의 출장.' },
  { t: '- 해외 및 팀장급 이상의 출장.', v: '대표' },
  { t: '- 국내 및 일반직원의 출장', v: '임원' },
  { c: '7. 직원의 휴가.' },
  { t: '- 팀장급 이상의 휴가.', v: '대표' },
  { t: '- 일반직원의 휴가.', v: '임원' },
  { t: '8. 직원의 휴직.', v: '대표' },
  { t: '9. 직원의 퇴직.', v: '대표' },
  { t: '10. 임시직 직원의 채용.', v: '대표' },
  { t: '11. 직원의 급여 결정.', v: '대표' },
  { s: '예산 및 자금' },
  { c: '1. 자산구입 결정.' },
  { t: '- 5백만원 이상.', v: '대표' },
  { t: '- 1백만원 이상 ~ 5백만원 미만', v: '임원' },
  { t: '- 기타', v: '팀장' },
  { c: '2. 일반경비 지출 결정.' },
  { t: '- 1백만원 이상.', v: '대표' },
  { t: '- 3십만원 이상 ~ 1백만원 미만', v: '임원' },
  { t: '- 기타', v: '팀장' },
  { c: '3. 직원의 임금 및 복지.' },
  { t: '- 직원에 대한 사택대여', v: '대표' },
  { t: '- 직원에 대한 자금 대여', v: '대표' },
  { t: '- 기타 규정에 정한 복지 집행', v: '임원' },
  { c: '4. 자금관리.' },
  { t: '- 자금일보.', v: '대표' },
  { t: '- 자금계획서.', v: '대표' },
  { c: '5. 자금의 집행 - 대체결의서.' },
  { t: '- 1백만원 이상.', v: '대표' },
  { t: '- 기타.', v: '임원' },
  { t: '- 지급원인을 승인받은 자금의 집행.', v: '임원' },
  { c: '6. 자금조달.' },
  { t: '- 차입금의 기채.', v: '대표' },
  { t: '- 차입금의 상환.', v: '대표' },
  { t: '- 자본금의 증자.', v: '대표' },
  { c: '7. 자금의 운용.' },
  { t: '- 5억원 이상.', v: '대표' },
  { t: '- 기타.', v: '임원' },
  { s: '총무 및 서무 업무' },
  { c: '1. 회의.' },
  { t: '- 이사회 개최 및 결과 보고', v: '대표' },
  { t: '- 주주총회 개최 및 결과 보고', v: '대표' },
  { t: '- 사내 회의 관리', v: '임원' },
  { t: '2. 소송.', v: '대표' },
  { c: '3. 주식에관한 업무.' },
  { t: '- 주식명의개서에 관한건 · 1만주 이상', v: '대표' },
  { t: '- 주식명의개서에 관한건 · 1만주 미만', v: '임원' },
  { t: '- 주식 질권설정에 관한건', v: '임원' },
  { t: '- 기타 주식관련 업무.', v: '대표' },
  { t: '4. 인장 각인·폐기 및 관리.', v: '대표' },
  { t: '5. 부동산 매입 및 임대차.', v: '대표' },
  { t: '6. 의전 및 전사 행사.', v: '임원' },
  { t: '7. 동산 및 부동산 관리 및 보험.', v: '임원' },
  { t: '8. 상업등기.', v: '임원' },
  { t: '9. 업무용 소모품의 구입 및 배부.', v: '팀장' },
  { t: '10. 문서수발 및 관리.', v: '팀장' },
  { s: '회계관련업무' },
  { c: '1. 대외문서 발송.' },
  { g: '4. 기획업무' },
  { t: '- 중요사항', v: '대표' },
  { t: '- 일반사항', v: '임원' },
  { t: '2. 국제업무에 관한 사항.', v: '대표' },
  { t: '3. 부서간 업무조정.', v: '대표' },
];

// 예산·회계규정 — (주)디엑스테크 (제정 2000.11.01 / 개정 2001.09.13). 원문 사규집 기준.
const BUDGET_RULES: RuleChapter[] = [
  { ch: '제1장 통칙', articles: [
    { t: '제1조 (목적)', b: '이 규정은 예산에 관한 사항, 계약에 관한 사항을 정함으로써 예산운용의 효율과 계약체결의 공정을 기함을 목적으로 한다.' },
    { t: '제2조 (예산의 구분)', b: '① 예산의 기능에 따라 자본예산과 관리업무비예산으로 구분하여 운용한다.\n② 자본예산이라 함은 고정자산의 취득예산을 말한다.\n③ 관리업무비예산이라 함은 인건비 및 경비예산을 말한다.' },
    { t: '제3조 (예산담당자)', b: '예산의 편성 및 통제 등에 관한 업무는 경영지원부장이 담당한다.' },
    { t: '제4조 (예산집행자)', b: '관리업무비예산 및 자본예산의 예산신청과 집행요청은 당해 부서장이 한다. 단, 직무전결상 위임된 경우에는 담당 팀장이 대행한다.' },
    { t: '제5조 (예산지급자)', b: '관리업무예산 및 자본예산의 지급업무를 담당하는 예산지급은 경영지원부 재무관리팀장이 한다.' },
  ]},
  { ch: '제2장 예산의 집행', articles: [
    { t: '제6조 (예산의 집행)', b: '① 예산을 집행하고자 할 때에는 지급원인행위품의서를 작성하여 전결권자의 승인을 얻는다.\n② 지급원인행위품의 시에 집행액을 확정하기 곤란한 경우에는 최고한도를 정하여 품의할 수 있다.\n③ 다음 각 호의 1에 해당하는 경우에는 지급원인행위품의를 생략할 수 있다.\n 1. 인건비\n 2. 경비 중 급식비·교통비·단련비·국내여비·업무추진비·기밀비·통신비\n 3. 기타 내규 및 법령 등에서 지급기준이 정하여진 비목' },
    { t: '제7조 (예산집행의 전결권)', b: '예산의 집행과 관련된 전결사항은 별도의 직무전결세칙에 의한다.' },
    { t: '제8조 (회계적용 기준)', b: '① 예산의 집행과 관련된 회계적용기준은 별도의 회계관리세칙에서 정한다.\n② 회계관리세칙과 관련된 세부사항은 사장이 정한다.' },
  ]},
  { ch: '제3장 계약', articles: [
    { t: '제9조 (계약의 방법)', b: '① 매매·용역 등의 계약을 하는 경우에는 공정한 절차를 거쳐 계약함을 원칙으로 한다. 다만, 계약의 목적·성질·조건 등에 비추어 필요한 경우에는 수의계약할 수 있다.\n② 제1항의 규정에 불구하고 다음 각 호의 1에 해당하는 경우에는 수의계약에 의한다.\n 1. 계약금액이 1백만원 이하인 공사계약\n 2. 계약금액이 1백만원 이하인 제조·구매·용역·기타 계약\n 3. 천재지변·긴급한 행사·비상재해 등으로 경쟁에 부칠 여유가 없을 경우\n 4. 계약의 목적을 비밀히 할 필요가 있을 경우\n 5. 국가·지방자치단체·정부투자기관 및 그 관계회사와 계약하는 경우 등\n 6.~13. 관계 법령에서 수의계약이 가능하도록 정한 경우 (세부 열거는 원문 참조)' },
    { t: '제10조 (계약서의 작성)', b: '계약을 체결하고자 할 때에는 계약서를 작성한다. 단, 다음 각 호의 1에 해당하는 경우에는 계약서 작성을 생략할 수 있다(이 경우 청구서·각서·협정서·승낙서 등 계약 근거 서류를 받아 비치한다).\n 1. 계약금액이 1천만원 이하인 계약\n 2. 경매에 부칠 경우\n 3. 물품 매각 시 즉시 대금을 납부하고 물품을 인수할 경우\n 4. 국가·지방자치단체·정부투자기관 및 그 관계회사와 계약할 경우\n 5. 전기·가스·수도 공급계약 등 성질상 계약서 작성이 불필요한 계약' },
    { t: '제11조 (계약보증금 등)', b: '① 계약을 체결할 때에는 계약상대자로 하여금 일정액의 보증금을 납부하게 하거나 보증서·보증보험증권·유가증권 등을 제출케 함을 원칙으로 한다.\n② 다음의 경우에는 보증금 납부 또는 보증서 등의 제출을 면제할 수 있다.\n 1. 계약금액이 3천만원을 초과하지 아니하는 계약\n 2. 관공서·정부투자기관 등과 계약을 체결할 때\n 3.~5. 법령상 면제 대상이거나 보증금 징수가 부적합하다고 인정될 경우' },
  ]},
  { ch: '제4장 회계단위', articles: [
    { t: '제12조 (회계단위)', b: '① 효율적인 회사운영을 위하여 회계단위를 구분하여 회계처리할 수 있다.\n② 사업부별 회계처리에 관한 세부적인 사항은 별도의 사업부별 회계단위세칙에서 정한다.\n③ 사업부별 회계관리세칙과 관련된 세부사항은 사장이 정한다.' },
  ]},
  { ch: '부칙 · 관련 세칙', articles: [
    { t: '제1조 (시행일)', b: '이 규정은 2000년 11월 1일부터 시행한다.' },
    { t: '제2조 (시행일)', b: '이 규정은 2001년 9월 13일부터 시행한다.' },
    { t: '※ 관련 세칙', b: '회계관리세칙 · 사업부별 회계단위세칙' },
  ]},
];

// 별표 · 예산비목표 — 관/항/목/처리내용. 원문 사규집 <별표> 기준. g: 관(대분류), h: 항(중분류), m/d: 목·처리내용
interface BudgetRow { g?: string; h?: string; m?: string; d?: string }
const BUDGET_TABLE: BudgetRow[] = [
  { g: '1. 관리업무비 예산비목' },
  { h: '인건비 (관: 관리업무비)' },
  { m: '임원급여', d: '임원급여(특별상여금 포함)' },
  { m: '급료와임금', d: '직원 본봉 및 직책수당' },
  { m: '상여금', d: '직원의 특별상여금' },
  { m: '성과급', d: '업무성과에 따라 지급되는 특별성과급' },
  { m: '제수당', d: '규정에서 정하는 제수당' },
  { m: '잡급', d: '자문위원수당, 계약직, 일용잡급, 고문료 등' },
  { m: '퇴직급여충당금', d: '임직원 퇴직충당금' },
  { h: '경비 (관: 관리업무비)' },
  { m: '복리후생비', d: '후생비(급식비·교통비), 복리비(야식비·보건의료비·학자보조금·체육비·그룹활동비·의료보험료·보증보험료·연성비·경조금·포상비·피복비·재형저축 임의장려금 등)' },
  { m: '여비교통비', d: '시외출장·시내출장·해외출장비·부임여비' },
  { m: '전산업무비', d: '전산시설유지보수비·DNS사용료·외부정보시스템 이용료·S/W구입 사용료·전산화 관련 용역비·전산소모품비·기타 전산화 관련 제비용' },
  { m: '연수비', d: '연구용역비(원고·강의·출제 등 용역에 대한 대가), 연수제비(국내외연수 수강료 및 등록금·제시험응시료·연수목적 세미나 참가비·국외연수 참가경비·연수에 따른 식대·기념품대·교제대·간담회비·연수시설 임차료·사용료·기타 제수수료)' },
  { m: '인쇄비', d: '간행물발간 및 인쇄비, 복사비(조사연구목적 간행물·광고목적 유인물제작비 제외)' },
  { m: '수선유지비', d: '업무용 동·부동산 및 집기 등의 수선유지비(차량·전산시설 수선유지비 및 자본적 지출 제외)' },
  { m: '업무추진비', d: '업무추진 제비용' },
  { m: '기밀비', d: '접대성 비용 중 기밀성격의 비용' },
  { m: '통신비', d: '전화료·전용회선사용료·우편전신료·택배이용료' },
  { m: '관리비', d: '사무실·공관관리비(전력수도료·연료비·세탁·화분관리비·오물세·청소료 등 포함. 공관수선 유지비 제외)' },
  { m: '임차료', d: '토지·건물 기타의 지급임차료' },
  { m: '차량비', d: '유류비·수리유지비·주차비·보험료·제세·차량보조·기타부대비 등' },
  { m: '소모품비', d: '업무에 필요한 소모성 집기 비품·기타 잡품·사무용 문방구 구입비 등 (전산시설 관련 소모품비 제외)' },
  { m: '회의비', d: '각종 회의·간담회 개최에 따른 경비' },
  { m: '용역비', d: '운전용역비·일반사무용역비·회계감사료·변호사 자문수수료·출제·번역·원고·감정 등의 용역대가(연수·조사연구 등과 관련한 출제 번역 원고 등은 제외)·중개수수료·심부름센터 이용료·송금수수료 등' },
  { m: '도서신문비', d: '신문·잡지·관보 구독료·제반서적·지도·기타 일반자료 구입비' },
  { m: '조사연구비', d: '조사연구목적 간행물 발간비(원고료·간행물 제작 용역비·인쇄비 등 포함)·조사연구를 주목적으로 한 세미나개최비·조사연 관련 용역대가·조사연구자료 수집비 등' },
  { m: '광고선전비', d: '대중매체를 통한 광고료·광고문안 제작비·영업안내서·팜플렛·현수막·포스터·간판·기타 홍보를 위한 각종 시청각자료 제작비(자본적 지출분 제외)·홍보용 기념품 및 판촉물의 제작비·기타 홍보·광고를 위한 비용·광고료' },
  { m: '보험료', d: '차량 이외의 고정자산·기타 설비에 대한 보험료' },
  { m: '세금과공과', d: '세금(법인세·주민세·교육세, 차량과 등기소송관계 세금은 제외)·협회비(공공 및 업무관계단체 회비 및 협회비 등)' },
  { m: '등기소송비', d: '등기 및 동수속비(자산취득에 따른 등기비는 제외)·등·사본료 및 열람수수료·법인인감증명료 등 제수수료·인지대·확정일자대·변호사 착수금·사례금 등 소송관계 제비용' },
  { m: '감가상각비', d: '업무용 동·부동산에 대한 감가상각충당금' },
  { m: '잡비', d: '타 비목으로 처리하기 곤란한 경비' },
  { g: '2. 자본예산 비목' },
  { h: '유형고정자산 (관: 고정자산)' },
  { m: '토지', d: '—' },
  { m: '건물', d: '—' },
  { m: '차량운반구', d: '각종 차량구입 및 부대설비 설치에 소요된 지출' },
  { m: '업무용동산', d: '사무기계 및 제기기류의 구입에 소요된 지출' },
  { m: '기타유형고정자산', d: '공기구비품으로서 자산으로 취급되고 내용연수 1년 이상, 거래단위 10만원 초과의 비품구입에 소요된 지출 / 타 비목으로 처리하기 곤란한 기타 유형 고정자산' },
  { h: '무형고정자산 (관: 고정자산)' },
  { m: '개발비', d: '게임 개발을 위한 개발비.' },
];

export function CompanyInfo(): JSX.Element {
  const info = useQuery({ queryKey: ['company-info'], queryFn: () => api.companyInfo() });
  const c = info.data?.info;

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 회사 기본 정보 */}
      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 13px', borderBottom: '1px solid #F3F4F6', fontSize: 15, fontWeight: 700 }}>회사 기본 정보</div>
        {info.isLoading || !c ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
        ) : (
          <div style={{ padding: 18, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1px', background: '#EDEFF2', border: '1px solid #EDEFF2', borderRadius: 10, overflow: 'hidden' }}>
            <Row k="법인명" v={c.name} />
            <Row k="사업자등록번호" v={c.bizNo} />
            <Row k="대표자" v={c.ceo} />
            <Row k="법인등록번호" v={c.corpNo} />
            <Row k="개업연월일" v={dDot(c.openDate)} />
            <Row k="전자세금계산서" v={c.taxEmail} />
            <Row k="소재지" v={c.address} span />
            <Row k="업태" v={c.bizType.join(' / ')} span />
            <Row k="종목" v={c.bizItem.join(' / ')} span />
          </div>
        )}
      </div>

      {/* 사규 (취업규칙) — 아코디언 */}
      <RuleAccordion
        title="사규 (취업규칙)"
        heading="취업규칙"
        enacted="제정 2016. 07. 22"
        preamble={RULE_PREAMBLE}
        chapters={COMPANY_RULES}
        footnote={<>※ (주)디엑스테크 <b>취업규칙</b> 본문 기준. 직제·예산·인사·급여복지·사규관리 규정 및 각 세칙(경조금·여비 등) 전문은 사규집(PDF)을 <b>문서함</b>에 등록하여 열람·다운로드합니다.</>}
      />

      {/* 휴가운영세칙 — 아코디언 */}
      <RuleAccordion
        title="사규 (휴가운영세칙)"
        heading="휴가운영세칙"
        enacted="제정 2016. 07. 22"
        chapters={LEAVE_RULES}
        footnote={<>※ 취업규칙 <b>제16조 ⑤</b>(휴가 관련 세부사항)에서 위임한 세칙 전문입니다. 원문상 휴가운영세칙 다음의 <b>사무인수세칙·직원연수세칙</b>은 본문이 미완성 상태(제목만 존재)라 반영에서 제외하며, 전문 확정 시 <b>문서함</b> 사규집(PDF)로 갈음합니다.</>}
      />

      {/* 직제규정 — 아코디언 */}
      <RuleAccordion
        title="사규 (직제규정)"
        heading="직제규정"
        enacted="제정 2016. 07. 22"
        chapters={ORG_RULES}
        footnote={<>※ 회사의 조직 및 직무권한을 규정한 <b>직제규정</b> 전문입니다. 원문에 온라인게임 사업부 등 <b>제정 당시 조직 편제</b>가 그대로 기재되어 있어, 현행 조직도(사업부·지원부 구성)에 맞춘 개정이 필요합니다.</>}
      />

      {/* 직무분장세칙 — 아코디언 */}
      <RuleAccordion
        title="사규 (직무분장세칙)"
        heading="직무분장세칙"
        enacted="제정 2000. 07. 01"
        chapters={JOB_ASSIGN_RULES}
        footnote={<>※ 직제규정 <b>제4조</b>(직무분장)에서 위임한 세칙 전문입니다. 각 사업부·지원부의 팀 구성 역시 <b>제정 당시 편제</b> 기준이므로 현행 조직에 맞춘 갱신이 필요합니다.</>}
      />

      {/* 직무전결세칙 — 조문 아코디언 + 별표1 전결기준 매트릭스 */}
      <RuleAccordion
        title="사규 (직무전결세칙)"
        heading="직무전결세칙"
        enacted="제정 2001. 02. 01"
        chapters={DELEG_RULES}
        footnote={<>※ 직제규정 <b>제5조</b>(직무전결)에서 위임한 세칙 전문입니다. 업무별 전결기준은 아래 <b>「별표1 · 업무별 전결기준」</b>에 정리했습니다.</>}
      />
      <DelegMatrixCard />

      {/* 예산·회계규정 — 조문 아코디언 + 별표 예산비목표 */}
      <RuleAccordion
        title="사규 (예산·회계규정)"
        heading="예산·회계 규정"
        enacted="제정 2000. 11. 01 · 개정 2001. 09. 13"
        chapters={BUDGET_RULES}
        footnote={<>※ 예산운용·계약·회계단위에 관한 규정입니다. 예산비목(계정과목) 분류는 아래 <b>「별표 · 예산비목표」</b>에 정리했습니다. 제9~11조의 수의계약·계약서 생략·보증금 면제 세부 열거 일부는 요약했으며 전문은 문서함 사규집(PDF)을 따릅니다.</>}
      />
      <BudgetTableCard />
    </div>
  );
}

// 별표 · 예산비목표 — 관/항/목/처리내용 계정과목표 (펼침/접힘 카드)
function BudgetTableCard(): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <button onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px 13px', border: 'none', borderBottom: open ? '1px solid #F3F4F6' : 'none', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>별표 · 예산비목표</span>
        <span style={{ fontSize: 11.5, color: '#9CA3AF' }}>예산·회계규정 부속</span>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#94A3B8', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
      </button>
      {open && (
        <div style={{ padding: 14 }}>
          <div style={{ overflowX: 'auto', border: '1px solid #EDEFF2', borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 480, fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  <th style={{ textAlign: 'left', padding: '9px 12px', width: 128, fontWeight: 700, color: '#334155', borderBottom: '1px solid #EDEFF2' }}>목 (계정과목)</th>
                  <th style={{ textAlign: 'left', padding: '9px 12px', fontWeight: 700, color: '#334155', borderBottom: '1px solid #EDEFF2', borderLeft: '1px solid #EDEFF2' }}>처리내용</th>
                </tr>
              </thead>
              <tbody>
                {BUDGET_TABLE.map((row, i) => {
                  if (row.g) return (
                    <tr key={i}><td colSpan={2} style={{ padding: '8px 12px', fontWeight: 700, fontSize: 13, color: '#1F2937', background: '#EEF2F7', borderTop: '1px solid #E2E8F0' }}>{row.g}</td></tr>
                  );
                  if (row.h) return (
                    <tr key={i}><td colSpan={2} style={{ padding: '6px 12px 6px 18px', fontWeight: 700, fontSize: 12, color: '#5B6472', background: '#F5F7FA' }}>{row.h}</td></tr>
                  );
                  return (
                    <tr key={i}>
                      <td style={{ padding: '7px 12px', fontWeight: 600, color: '#475569', borderTop: '1px solid #F3F4F6', verticalAlign: 'top' }}>{row.m}</td>
                      <td style={{ padding: '7px 12px', color: '#4B5563', lineHeight: 1.6, borderTop: '1px solid #F3F4F6', borderLeft: '1px solid #F3F4F6' }}>{row.d}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, lineHeight: 1.6 }}>
            ※ 원문 <b>&lt;별표&gt; 예산비목표</b>의 관/항/목/처리내용을 옮긴 표입니다. 자본예산 무형고정자산의 <b>「게임 개발을 위한 개발비」</b> 등 제정 당시 업종 기준 항목은 현행 사업에 맞춘 개정이 필요합니다.
          </div>
        </div>
      )}
    </div>
  );
}

// 별표1 · 업무별 전결기준 — 대표이사/임원/팀장 전결권자 매트릭스 (펼침/접힘 카드)
function DelegMatrixCard(): JSX.Element {
  const [open, setOpen] = useState(false);
  const dot = (row: AuthRow, lv: AuthLv): string => (row.v === lv ? '●' : '');
  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <button onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px 13px', border: 'none', borderBottom: open ? '1px solid #F3F4F6' : 'none', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>별표1 · 업무별 전결기준</span>
        <span style={{ fontSize: 11.5, color: '#9CA3AF' }}>직무전결세칙 부속</span>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#94A3B8', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
      </button>
      {open && (
        <div style={{ padding: 14 }}>
          <div style={{ overflowX: 'auto', border: '1px solid #EDEFF2', borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 460, fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  <th style={{ textAlign: 'left', padding: '9px 12px', fontWeight: 700, color: '#334155', borderBottom: '1px solid #EDEFF2', position: 'sticky', left: 0, background: '#F8FAFC' }}>직무전결사항</th>
                  {(['대표이사', '임원', '팀장'] as const).map((h) => (
                    <th key={h} style={{ width: 62, padding: '9px 4px', fontWeight: 700, color: '#334155', borderBottom: '1px solid #EDEFF2', borderLeft: '1px solid #EDEFF2' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DELEG_MATRIX.map((row, i) => {
                  if (row.g) return (
                    <tr key={i}><td colSpan={4} style={{ padding: '8px 12px', fontWeight: 700, fontSize: 13, color: '#1F2937', background: '#EEF2F7', borderTop: '1px solid #E2E8F0' }}>{row.g}</td></tr>
                  );
                  if (row.s) return (
                    <tr key={i}><td colSpan={4} style={{ padding: '6px 12px 6px 20px', fontWeight: 700, fontSize: 12, color: '#5B6472', background: '#F5F7FA' }}>&lt;{row.s}&gt;</td></tr>
                  );
                  if (row.c) return (
                    <tr key={i}><td colSpan={4} style={{ padding: '6px 12px', fontWeight: 600, color: '#475569', borderTop: '1px solid #F3F4F6' }}>{row.c}</td></tr>
                  );
                  const leaf = row.t ?? '';
                  const indent = leaf.startsWith('-');
                  return (
                    <tr key={i}>
                      <td style={{ padding: '6px 12px', paddingLeft: indent ? 26 : 12, color: '#4B5563', borderTop: '1px solid #F3F4F6', position: 'sticky', left: 0, background: '#fff' }}>{leaf}</td>
                      {(['대표', '임원', '팀장'] as AuthLv[]).map((lv) => (
                        <td key={lv} style={{ textAlign: 'center', color: '#2563EB', fontSize: 12, borderTop: '1px solid #F3F4F6', borderLeft: '1px solid #F3F4F6' }}>{dot(row, lv)}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, lineHeight: 1.6 }}>
            ※ 원문 <b>&lt;별표1&gt;</b>의 전결권자(●) 표기를 그대로 옮긴 표입니다. 구분·직무전결사항은 <b>제정 당시(2001년) 조직·금액 기준</b>이므로 현행 조직 및 전결 한도에 맞춘 개정이 필요합니다.
          </div>
        </div>
      )}
    </div>
  );
}

// 장(章) 아코디언 카드 — 취업규칙·세칙 등 조문 규정을 공통 렌더링. 각 카드가 독립 펼침 상태를 가짐.
function RuleAccordion(
  { title, heading, enacted, preamble, chapters, footnote }:
  { title: string; heading: string; enacted: string; preamble?: string; chapters: RuleChapter[]; footnote?: JSX.Element },
): JSX.Element {
  const [openChapters, setOpenChapters] = useState<Set<number>>(new Set());
  const toggle = (i: number): void =>
    setOpenChapters((prev) => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const allOpen = openChapters.size === chapters.length;
  const toggleAll = (): void => setOpenChapters(allOpen ? new Set() : new Set(chapters.map((_, i) => i)));

  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px 13px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
        <button className="dx-btn dx-btn-ghost" style={{ marginLeft: 'auto', padding: '5px 12px', fontSize: 12.5 }} onClick={toggleAll}>
          {allOpen ? '모두 접기' : '모두 펼치기'}
        </button>
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ fontSize: 12.5, color: '#4B5563', lineHeight: 1.75, background: '#F8FAFC', border: '1px solid #EDEFF2', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: '#1F2937', marginBottom: preamble ? 6 : 0 }}>{heading} <span style={{ fontWeight: 500, color: '#9CA3AF', fontSize: 11.5 }}>({enacted})</span></div>
          {preamble}
        </div>
        {chapters.map((chap, i) => {
          const open = openChapters.has(i);
          return (
            <div key={i} style={{ border: '1px solid #EDEFF2', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
              <button onClick={() => toggle(i)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', border: 'none', background: open ? '#F8FAFC' : '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#1F2937', flex: 1 }}>{chap.ch}</span>
                <span style={{ fontSize: 11.5, color: '#9CA3AF' }}>{chap.articles.length}개 조</span>
                <span style={{ fontSize: 13, color: '#94A3B8', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
              </button>
              {open && (
                <div style={{ padding: '4px 16px 14px', borderTop: '1px solid #F3F4F6', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {chap.articles.map((a, j) => (
                    <div key={j} style={{ paddingTop: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 4 }}>{a.t}</div>
                      <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{a.b}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {footnote && (
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, lineHeight: 1.6 }}>{footnote}</div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, span }: { k: string; v: string; span?: boolean }): JSX.Element {
  return (
    <div style={{ background: '#fff', padding: '11px 14px', display: 'flex', gap: 12, gridColumn: span ? 'span 2' : undefined }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', width: 96, flexShrink: 0 }}>{k}</span>
      <span style={{ fontSize: 13, color: '#374151' }}>{v || '—'}</span>
    </div>
  );
}
